/**
 * mc-cookie-consent.js — consentimento de cookies (task 0037)
 *
 * Maquina de estado da barra nao bloqueante (DP-1):
 *   - espera a resolucao do Age Gate antes de aparecer (DP-10) — lendo o
 *     mesmo localStorage `mc_age_verified` e observando o atributo hidden
 *     de #age-gate. NUNCA escreve em nada do age gate (DIFF ZERO em F-6).
 *   - persiste a decisao em `mc_cookie_consent` com o TTL definido pelo dono
 *     em settings.consent_ttl_hours (clamp 1..8760h, default 4320h = 180
 *     dias — DP-11, simetrico entre aceite e recusa). Expirada, a decisao
 *     vale como inexistente: a barra volta e o GTM nao carrega ate um novo
 *     "Aceitar". Todo acesso a storage vai em try/catch — em aba anonima a
 *     barra continua funcionando, valendo so para a pagina atual.
 *   - reabre por LINK, sem nenhum elemento flutuante no site (o botao de
 *     preferencias foi removido a pedido do dono em 2026-08-10): basta a URL
 *     conter o hash `#preferencias-cookies` ou o parametro
 *     `?cookies=preferencias`. Funciona em qualquer pagina, abre mesmo com
 *     decisao valida vigente, e o gatilho e limpo da URL com replaceState
 *     para nao reabrir a cada refresh. E o procedimento facilitado de
 *     revogacao que a LGPD art. 8º §5º exige.
 *   - injeta o GTM na sessao atual, SEM reload, pelo unico ponto de injecao
 *     (window.mcConsent.loadGtm, definido no snippet mc-gtm-head) — §4.5.
 *   - propaga a decisao para a Customer Privacy API da Shopify em TODA page
 *     load (DP-6/DP-7). Falha assimetrica (DP-8): no aceite nao bloqueia
 *     nada; na recusa ha retry (3 tentativas em ate 10s), `propagated:false`
 *     persistido e reconciliacao na proxima navegacao.
 *   - revogacao (aceito -> recusado) recarrega a pagina (DP-15); o caminho
 *     inverso nao recarrega.
 *   - telemetria: um `cookie_consent` por decisao, sem PII (DP-18).
 *
 * Offset dinamico --mc-consent-safe-bottom (gate F0, AJ-7): a barra cede
 * espaco para a buybar de compra da PDP/bundle enquanto ela estiver visivel.
 * Escrito no documentElement — nenhum seletor alheio e tocado.
 *
 * Hooks (nao renomear): #mc-consent, [data-mc-consent-accept],
 * [data-mc-consent-decline], data-mc-consent-ttl
 */

(function () {
  'use strict';

  var bar = document.getElementById('mc-consent');
  if (!bar) return;

  var acceptBtn = bar.querySelector('[data-mc-consent-accept]');
  var declineBtn = bar.querySelector('[data-mc-consent-decline]');
  if (!acceptBtn || !declineBtn) return;

  var api = window.mcConsent || null;
  var VERSION = api && api.version ? api.version : 1;
  var KEY = api && api.storageKey ? api.storageKey : 'mc_cookie_consent';
  var DESIGN = api ? api.designMode === true : bar.getAttribute('data-mc-consent-design') === 'true';

  /* TTL: sempre o valor ja clampado pelo Liquid (1..8760h). O atributo do
     markup e a fonte de fallback — nao ha constante de tempo neste arquivo,
     senao trocar a janela no Theme Editor deixaria o asset mentindo. */
  var TTL_MS = api && api.ttlMs ? api.ttlMs : parseInt(bar.getAttribute('data-mc-consent-ttl'), 10);
  if (!TTL_MS || isNaN(TTL_MS) || TTL_MS <= 0) TTL_MS = 4320 * 3600000;

  /* Gatilho de reabertura por link (sem elemento flutuante) */
  var REOPEN_HASH = '#preferencias-cookies';
  var REOPEN_PARAM = 'cookies';
  var REOPEN_VALUE = 'preferencias';

  var AGE_KEY = 'mc_age_verified';
  var AGE_TTL_MS = 30 * 24 * 60 * 60 * 1000;

  var ATTEMPT_TIMEOUT = 2500;
  var RETRY_GAPS = [0, 500, 1500];
  var REVOKE_FAILSAFE = 10000;
  var CLOSE_FAILSAFE = 260;

  /* estado em memoria — unica fonte quando o localStorage nao esta disponivel */
  var runtimeState = api && api.state ? api.state : null;
  var closeTimer = null;

  /* ── Preferencia de movimento (lida no momento do uso) ──────────── */
  function prefersReduced() {
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) {
      return false;
    }
  }

  /* ── Persistencia ───────────────────────────────────────────────── */
  function isValidState(parsed) {
    if (!parsed || parsed.v !== VERSION || typeof parsed.ts !== 'number') return false;
    if (parsed.status !== 'accepted' && parsed.status !== 'rejected') return false;
    var age = Date.now() - parsed.ts;
    return age >= 0 && age < TTL_MS;
  }

  function readState() {
    try {
      var raw = window.localStorage.getItem(KEY);
      if (raw) {
        var parsed = JSON.parse(raw);
        if (isValidState(parsed)) return parsed;
      }
      return null;
    } catch (e) {
      return isValidState(runtimeState) ? runtimeState : null;
    }
  }

  function writeState(state) {
    runtimeState = state;
    if (api) api.state = state;
    try {
      window.localStorage.setItem(KEY, JSON.stringify(state));
    } catch (e) {
      /* aba anonima ou storage bloqueado — a decisao vale para esta pagina */
    }
  }

  function markPropagated() {
    var state = readState();
    if (!state || state.propagated === true) return;
    state.propagated = true;
    writeState(state);
  }

  /* ── Telemetria (sem PII — DP-18) ───────────────────────────────── */
  function pushConsentEvent(status, source, wasAccepted) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'cookie_consent',
        consent_status: status === 'rejected' && wasAccepted ? 'revoked' : status,
        consent_source: source,
        consent_version: VERSION
      });
    } catch (e) {
      /* telemetria nunca bloqueia a decisao */
    }
  }

  /* ── Customer Privacy API (DP-6/DP-7/DP-8) ──────────────────────── */
  function setConsent(status, callback) {
    var value = status === 'accepted';
    var signals = {
      analytics: value,
      marketing: value,
      preferences: value,
      sale_of_data: value
    };
    var settled = false;

    function finish(ok) {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      callback(ok === true);
    }

    var timer = window.setTimeout(function () {
      finish(false);
    }, ATTEMPT_TIMEOUT);

    try {
      if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') {
        finish(false);
        return;
      }
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
        if (error) {
          finish(false);
          return;
        }
        try {
          var privacy = window.Shopify.customerPrivacy;
          if (!privacy || typeof privacy.setTrackingConsent !== 'function') {
            finish(false);
            return;
          }
          privacy.setTrackingConsent(signals, function (response) {
            finish(!(response && response.error));
          });
        } catch (inner) {
          finish(false);
        }
      });
    } catch (outer) {
      finish(false);
    }
  }

  function propagate(status, onDone) {
    if (DESIGN) {
      if (onDone) onDone(false);
      return;
    }
    var gaps = status === 'rejected' ? RETRY_GAPS : [0];
    var index = 0;

    /* Carimbo da decisao que originou esta cadeia. Com a barra reaberta por
       link, uma propagacao de page load pode estar em voo (ate ~9,5s de
       retry) quando o usuario clica — sem este guard, a cadeia velha voltaria
       a mandar o sinal ANTIGO depois do clique e ainda marcaria a decisao
       nova como propagada. */
    var origin = readState();
    var stamp = origin ? origin.ts : null;

    function attempt() {
      setConsent(status, function (ok) {
        if (stamp !== null) {
          var now = readState();
          if (!now || now.ts !== stamp) {
            if (onDone) onDone(false);
            return;
          }
        }
        if (ok) {
          markPropagated();
          if (onDone) onDone(true);
          return;
        }
        index += 1;
        if (index < gaps.length) {
          window.setTimeout(attempt, gaps[index]);
        } else if (onDone) {
          onDone(false);
        }
      });
    }

    attempt();
  }

  /* ── Offset contra a buybar de compra (AJ-7) ────────────────────── */
  function initSafeBottom() {
    var obstruction = document.querySelector('.mc-pdp-buybar');
    if (!obstruction) return;

    function sync() {
      var height = obstruction.classList.contains('is-visible') ? obstruction.offsetHeight : 0;
      document.documentElement.style.setProperty('--mc-consent-safe-bottom', height + 'px');
    }

    sync();

    try {
      new MutationObserver(sync).observe(obstruction, {
        attributes: true,
        attributeFilter: ['class']
      });
    } catch (e) {
      /* sem MutationObserver o offset fica no valor inicial */
    }

    try {
      if (window.ResizeObserver) new window.ResizeObserver(sync).observe(obstruction);
    } catch (e) {
      /* opcional */
    }

    window.addEventListener('resize', sync);
  }

  /* ── Age Gate (DP-10) — leitura, nunca escrita ──────────────────── */
  function isAgeVerified() {
    try {
      var raw = window.localStorage.getItem(AGE_KEY);
      if (!raw) return false;
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed.ts !== 'number') return false;
      var age = Date.now() - parsed.ts;
      return age >= 0 && age < AGE_TTL_MS;
    } catch (e) {
      return false;
    }
  }

  function afterAgeGate(callback) {
    var gate = document.getElementById('age-gate');
    if (!gate || isAgeVerified()) {
      callback();
      return;
    }
    try {
      var observer = new MutationObserver(function () {
        if (gate.hasAttribute('hidden')) {
          observer.disconnect();
          callback();
        }
      });
      observer.observe(gate, { attributes: true, attributeFilter: ['hidden'] });
    } catch (e) {
      callback();
    }
  }

  /* ── Gatilho de reabertura por link (DP-14 sem pegada visual) ───── */
  function hasReopenTrigger() {
    try {
      if (window.location.hash === REOPEN_HASH) return true;
      var search = window.location.search || '';
      if (search.indexOf(REOPEN_PARAM + '=' + REOPEN_VALUE) === -1) return false;
      if (window.URLSearchParams) {
        return new window.URLSearchParams(search).get(REOPEN_PARAM) === REOPEN_VALUE;
      }
      return true;
    } catch (e) {
      return false;
    }
  }

  /* Limpa o gatilho da URL: sem isso um F5 reabriria a barra para sempre.
     Se replaceState nao existir, a barra apenas reabre de novo — degradacao
     aceitavel, nunca erro. */
  function clearReopenTrigger() {
    try {
      if (!window.history || typeof window.history.replaceState !== 'function') return;
      var url = new window.URL(window.location.href);
      url.searchParams.delete(REOPEN_PARAM);
      /* Sai o hash DO GATILHO; qualquer outro fragmento e preservado. Sem
         essa distincao, um link de preferencias combinado com uma ancora do
         tema (#faq-q-N da 0015, #descricao da 0026, ancoras da home 0036)
         destruiria a ancora ao limpar o gatilho. */
      var hash = url.hash === REOPEN_HASH ? '' : url.hash;
      var clean = url.pathname + (url.search || '') + hash;
      window.history.replaceState(null, '', clean);
    } catch (e) {
      /* URL/searchParams indisponivel — segue com a URL como esta */
    }
  }

  /* ── UI ─────────────────────────────────────────────────────────── */
  function markCurrent() {
    var state = readState();
    acceptBtn.classList.remove('is-current');
    declineBtn.classList.remove('is-current');
    if (!state) {
      acceptBtn.removeAttribute('aria-pressed');
      declineBtn.removeAttribute('aria-pressed');
      return;
    }
    var accepted = state.status === 'accepted';
    acceptBtn.setAttribute('aria-pressed', accepted ? 'true' : 'false');
    declineBtn.setAttribute('aria-pressed', accepted ? 'false' : 'true');
    (accepted ? acceptBtn : declineBtn).classList.add('is-current');
  }

  /* fromLink = aberta pelo gatilho de preferencias; so nesse caso o foco vai
     para a barra, porque a abertura foi iniciada pelo usuario e sem foco ela
     ficaria inalcançavel por teclado sem re-tabular a pagina inteira. Na
     primeira visita a barra NUNCA rouba o foco (DP-1 / §4.7). */
  function openBar(fromLink) {
    window.clearTimeout(closeTimer);
    deciding = false;
    acceptBtn.removeAttribute('aria-disabled');
    declineBtn.removeAttribute('aria-disabled');
    declineBtn.removeAttribute('aria-busy');
    bar.classList.remove('is-closing');
    bar.hidden = false;
    markCurrent();
    if (fromLink) {
      try {
        declineBtn.focus({ preventScroll: true });
      } catch (e) {
        declineBtn.focus();
      }
    }
  }

  function closeBar() {
    var hadFocus = bar.contains(document.activeElement);
    var finished = false;

    function finish() {
      if (finished) return;
      finished = true;
      window.clearTimeout(closeTimer);
      bar.hidden = true;
      bar.classList.remove('is-closing');
      /* Sem botao flutuante nao ha sucessor natural do controle destruido.
         Quem decidiu por teclado vai para o conteudo principal (#MainContent
         ja tem tabindex="-1" no theme.liquid); deixar o foco cair num
         elemento `hidden` derrubaria o contexto do leitor de tela. Quem
         decidiu com mouse nao tem foco na barra e nada e tocado. */
      if (hadFocus) {
        var main = document.getElementById('MainContent');
        try {
          if (main) main.focus({ preventScroll: true });
          else if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
        } catch (e) {
          if (main) main.focus();
        }
      }
    }

    if (prefersReduced()) {
      finish();
      return;
    }

    bar.classList.add('is-closing');
    bar.addEventListener('animationend', finish, { once: true });
    closeTimer = window.setTimeout(finish, CLOSE_FAILSAFE);
  }

  /* ── Decisao ────────────────────────────────────────────────────── */
  /* Guarda de reentrancia: a barra continua clicavel durante os ~200ms da
     animacao de fecho, e `aria-disabled` e apenas declarativo. Sem esta
     trava, um duplo clique (ou duplo toque) emite DOIS eventos
     `cookie_consent` para UMA decisao e infla a taxa de consentimento —
     a metrica primaria da task (criterio de aceite 23). */
  var deciding = false;

  function decide(status, source) {
    if (deciding) return;
    deciding = true;

    acceptBtn.setAttribute('aria-disabled', 'true');
    declineBtn.setAttribute('aria-disabled', 'true');

    if (DESIGN) {
      closeBar();
      return;
    }

    var previous = readState();
    var wasAccepted = !!(previous && previous.status === 'accepted');

    writeState({ v: VERSION, status: status, ts: Date.now(), propagated: false });
    pushConsentEvent(status, source, wasAccepted);

    if (status === 'accepted') {
      if (api) {
        api.accepted = true;
        api.loadGtm();
      }
      propagate('accepted');
      closeBar();
      return;
    }

    if (api) api.accepted = false;

    if (wasAccepted) {
      /* revogacao: nao existe "desinjetar" GTM — recarrega (DP-15) */
      declineBtn.setAttribute('aria-busy', 'true');
      var reloaded = false;
      var reload = function () {
        if (reloaded) return;
        reloaded = true;
        window.location.reload();
      };
      window.setTimeout(reload, REVOKE_FAILSAFE);
      propagate('rejected', reload);
      return;
    }

    propagate('rejected');
    closeBar();
  }

  /* ── Ligacao ────────────────────────────────────────────────────── */
  /* origem da decisao para a telemetria (DP-18): `link` quando a barra foi
     reaberta pelo gatilho de preferencias, `banner` na exibicao espontanea */
  var source = 'banner';

  acceptBtn.addEventListener('click', function () {
    decide('accepted', source);
  });

  declineBtn.addEventListener('click', function () {
    decide('rejected', source);
  });

  function openFromLink() {
    source = 'link';
    openBar(true);
    clearReopenTrigger();
  }

  /* link clicado na propria pagina (hash muda sem recarregar) */
  window.addEventListener('hashchange', function () {
    if (hasReopenTrigger()) openFromLink();
  });

  /* ── Arranque ───────────────────────────────────────────────────── */
  function start() {
    initSafeBottom();

    if (DESIGN) {
      afterAgeGate(function () {
        openBar(false);
      });
      return;
    }

    /* o gatilho de preferencias vence a decisao vigente: e por ele que o
       usuario revoga antes de o TTL expirar */
    if (hasReopenTrigger()) {
      var current = readState();
      if (current) {
        if (current.status === 'accepted' && api) {
          api.accepted = true;
          api.loadGtm();
        }
        /* DP-6 vale AQUI TAMBEM: a barra e nao bloqueante, entao o usuario
           pode abrir o aviso pelo link e simplesmente continuar navegando
           sem decidir. Sem esta chamada, essa page load inteira ficaria sem
           o sinal de consentimento na Customer Privacy API — e quem havia
           RECUSADO voltaria a ser rastreado pelos pixels da Shopify ate a
           proxima navegacao. Mesmo tratamento do caminho normal abaixo. */
        propagate(current.status);
      }
      afterAgeGate(openFromLink);
      return;
    }

    var state = readState();

    if (state) {
      if (state.status === 'accepted' && api) {
        api.accepted = true;
        api.loadGtm();
      }
      /* re-propaga em toda page load — reconcilia storage x cookie da Shopify */
      propagate(state.status);
      return;
    }

    afterAgeGate(function () {
      openBar(false);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
